// Planungsregeln als reine Funktionen.
// Sie spiegeln die Regeln der Datenbank (supabase/migrations) für die Oberfläche:
// Die Oberfläche zeigt damit sofort, wer frei ist. Verbindlich bleibt die Datenbank.
import type { Stand, Id, Datum, Uhrzeit, Einsatz, EinsatzEntwurf, KursKnoten, Dozent, Kurs, Zeitfenster, Raum } from './typen';
import { zeitpunkt, ueberschneidet, montag, plusTage, wochentag } from './zeit';

export interface Regelverstoss { code: string; detail?: string }

const FEHLERTEXTE: Record<string, string> = {
  DOPPELBELEGUNG_DOZENT: 'Der Dozent ist zu dieser Zeit bereits eingeplant.',
  DOPPELBELEGUNG_RAUM: 'Der Raum ist zu dieser Zeit bereits belegt.',
  UEBERSCHNEIDUNG_KURS: 'Der Kurs hat zu dieser Zeit bereits einen Termin.',
  DOZENT_ABWESEND: 'Der Dozent hat für diese Zeit eine Abwesenheit eingetragen.',
  DOZENT_NICHT_QUALIFIZIERT: 'Der Dozent ist für diesen Fachbereich nicht hinterlegt.',
  DOZENT_INAKTIV: 'Der Dozent ist deaktiviert.',
  KALENDER_GESPERRT: 'An diesem Tag findet kein Unterricht statt.',
  AUSSERHALB_KURSZEITRAUM: 'Der Termin liegt außerhalb der Kurslaufzeit.',
  KNOTEN_NICHT_IM_KURS: 'Das Thema gehört nicht zu diesem Kurs.',
  ZU_WENIG_UE: 'Ein Einsatz umfasst mindestens 4 Unterrichtseinheiten.',
  ZEIT_UNGUELTIG: 'Das Ende muss nach dem Beginn liegen.',
  RAUM_BEI_ONLINE: 'Online-Termine haben keinen Raum.',
  KEIN_EIGENER_EINSATZ: 'Dieser Einsatz gehört nicht zu Ihnen.',
  EINSATZ_NICHT_FREIGEGEBEN: 'Nur freigegebene Einsätze können abgesagt werden.',
  EINSATZ_VERGANGEN: 'Der Einsatz liegt in der Vergangenheit.',
  KEIN_ADMIN: 'Dafür fehlen Ihnen die Rechte.',
  RLS: 'Dafür fehlen Ihnen die Rechte.',
  UNBEKANNT: 'Die Änderung konnte nicht gespeichert werden.',
};
export function fehlerText(v: Regelverstoss): string {
  const t = FEHLERTEXTE[v.code] ?? FEHLERTEXTE.UNBEKANNT;
  return v.detail ? `${t} (${v.detail})` : t;
}
/** Übersetzt eine Fehlermeldung der Datenbank in einen Regelcode. */
export function dbFehler(meldung: string, detail?: string): Regelverstoss {
  if (meldung.includes('keine_doppelbelegung_dozent')) return { code: 'DOPPELBELEGUNG_DOZENT' };
  if (meldung.includes('keine_doppelbelegung_raum')) return { code: 'DOPPELBELEGUNG_RAUM' };
  if (meldung.includes('keine_ueberschneidung_kurs')) return { code: 'UEBERSCHNEIDUNG_KURS' };
  if (meldung.includes('einsatz_ue_check')) return { code: 'ZU_WENIG_UE' };
  if (meldung.includes('row-level security')) return { code: 'RLS' };
  const code = Object.keys(FEHLERTEXTE).find(k => meldung.startsWith(k));
  return { code: code ?? 'UNBEKANNT', detail: code ? detail : meldung };
}

// ------------------------------------------------------------------ Lehrplan
export function kinderVon(knoten: KursKnoten[]): Map<Id | null, KursKnoten[]> {
  const m = new Map<Id | null, KursKnoten[]>();
  for (const k of knoten) {
    const l = m.get(k.parent_id) ?? []; l.push(k); m.set(k.parent_id, l);
  }
  for (const l of m.values()) l.sort((a, b) => a.sortierung - b.sortierung);
  return m;
}
export function knotenFachbereich(stand: Stand, knotenId: Id | null): Id | null {
  let k = stand.kursKnoten.find(x => x.id === knotenId);
  while (k) {
    if (k.fachbereich_id) return k.fachbereich_id;
    const pid: Id | null = k.parent_id;
    k = pid ? stand.kursKnoten.find(x => x.id === pid) : undefined;
  }
  return null;
}
/** Fachbereichsknoten (oberster Knoten mit Fachbereich) zu einem Knoten */
export function fachbereichsKnoten(stand: Stand, knotenId: Id | null): KursKnoten | undefined {
  let k = stand.kursKnoten.find(x => x.id === knotenId);
  let treffer: KursKnoten | undefined;
  while (k) {
    if (k.fachbereich_id) treffer = k;
    const pid: Id | null = k.parent_id;
    k = pid ? stand.kursKnoten.find(x => x.id === pid) : undefined;
  }
  return treffer;
}
export function knotenPfad(stand: Stand, knotenId: Id | null): KursKnoten[] {
  const pfad: KursKnoten[] = [];
  let k = stand.kursKnoten.find(x => x.id === knotenId);
  while (k) {
    pfad.unshift(k);
    const pid: Id | null = k.parent_id;
    k = pid ? stand.kursKnoten.find(x => x.id === pid) : undefined;
  }
  return pfad;
}

export interface SollIstWert { soll: number; geplant: number; freigegeben: number }
export function sollIst(stand: Stand, kursId: Id): Map<Id, SollIstWert> {
  const knoten = stand.kursKnoten.filter(k => k.kurs_id === kursId);
  const kinder = kinderVon(knoten);
  const direkt = new Map<Id, { g: number; f: number }>();
  for (const e of stand.einsaetze) {
    if (e.kurs_id !== kursId || !e.kurs_knoten_id || e.status === 'entfaellt') continue;
    const d = direkt.get(e.kurs_knoten_id) ?? { g: 0, f: 0 };
    d.g += e.ue; if (e.status === 'freigegeben') d.f += e.ue;
    direkt.set(e.kurs_knoten_id, d);
  }
  const erg = new Map<Id, SollIstWert>();
  const rechne = (k: KursKnoten): SollIstWert => {
    const d = direkt.get(k.id) ?? { g: 0, f: 0 };
    const w = { soll: k.soll_ue ?? 0, geplant: d.g, freigegeben: d.f };
    for (const c of kinder.get(k.id) ?? []) { const cw = rechne(c); w.geplant += cw.geplant; w.freigegeben += cw.freigegeben; }
    erg.set(k.id, w); return w;
  };
  for (const w of kinder.get(null) ?? []) rechne(w);
  return erg;
}
/** Blätter des Lehrplans in Unterrichtsreihenfolge */
export function blaetter(stand: Stand, kursId: Id, unterhalb: Id | null = null): KursKnoten[] {
  const kinder = kinderVon(stand.kursKnoten.filter(k => k.kurs_id === kursId));
  const out: KursKnoten[] = [];
  const lauf = (pid: Id | null) => {
    for (const k of kinder.get(pid) ?? []) { if (kinder.has(k.id)) lauf(k.id); else out.push(k); }
  };
  if (unterhalb && !kinder.has(unterhalb)) { const k = stand.kursKnoten.find(x => x.id === unterhalb); return k ? [k] : []; }
  lauf(unterhalb);
  return out;
}
export function naechsterOffenerKnoten(stand: Stand, kursId: Id, unterhalb: Id | null = null): KursKnoten | undefined {
  const si = sollIst(stand, kursId);
  return blaetter(stand, kursId, unterhalb).find(b => { const w = si.get(b.id); return w && w.geplant < w.soll; });
}

// ------------------------------------------------------------------ Kalender, Zeitraster
export function kalenderSperre(stand: Stand, datum: Datum, standortId: Id | null) {
  return stand.kalender.find(t => t.datum === datum && (t.standort_id === null || t.standort_id === standortId));
}
export function kursFenster(stand: Stand, kurs: Kurs): Zeitfenster[] {
  return stand.zeitfenster.filter(z => z.zeitraster_id === kurs.zeitraster_id).sort((a, b) => a.sortierung - b.sortierung);
}
export function istUnterrichtstag(stand: Stand, kurs: Kurs, datum: Datum): boolean {
  return datum >= kurs.beginn && datum <= kurs.ende && wochentag(datum) < 5 && !kalenderSperre(stand, datum, kurs.standort_id);
}

// ------------------------------------------------------------------ Konflikte
const aktiv = (e: Einsatz) => e.status !== 'entfaellt';
export function abwesenheitZu(stand: Stand, dozentId: Id, datum: Datum, beginn: Uhrzeit, ende: Uhrzeit) {
  const von = zeitpunkt(datum, beginn), bis = zeitpunkt(datum, ende);
  return stand.abwesenheiten.find(a => a.dozent_id === dozentId && ueberschneidet(a.von, a.bis, von, bis));
}
export function einsatzZu(stand: Stand, feld: 'dozent_id' | 'raum_id' | 'kurs_id', wert: Id, datum: Datum, beginn: Uhrzeit, ende: Uhrzeit, ohne?: Id) {
  const von = zeitpunkt(datum, beginn), bis = zeitpunkt(datum, ende);
  return stand.einsaetze.find(e => e[feld] === wert && e.id !== ohne && aktiv(e) && e.datum === datum
    && ueberschneidet(zeitpunkt(e.datum, e.beginn), zeitpunkt(e.datum, e.ende), von, bis));
}
export function istQualifiziert(stand: Stand, dozentId: Id, knotenId: Id | null): boolean {
  const fb = knotenFachbereich(stand, knotenId);
  return !fb || stand.qualifikationen.some(q => q.dozent_id === dozentId && q.fachbereich_id === fb);
}

/** Prüft einen Einsatz gegen alle Regeln. Gibt den ersten Verstoß zurück oder null. */
export function pruefeEinsatz(stand: Stand, e: EinsatzEntwurf): Regelverstoss | null {
  const kurs = stand.kurse.find(k => k.id === e.kurs_id);
  if (!kurs) return { code: 'UNBEKANNT', detail: 'Kurs fehlt' };
  if (e.ue < 4) return { code: 'ZU_WENIG_UE' };
  if (e.ende <= e.beginn) return { code: 'ZEIT_UNGUELTIG' };
  if (e.form === 'online' && e.raum_id) return { code: 'RAUM_BEI_ONLINE' };
  if (e.status === 'entfaellt') return null;
  if (e.datum < kurs.beginn || e.datum > kurs.ende) return { code: 'AUSSERHALB_KURSZEITRAUM' };
  if (e.kurs_knoten_id && !stand.kursKnoten.some(k => k.id === e.kurs_knoten_id && k.kurs_id === e.kurs_id)) return { code: 'KNOTEN_NICHT_IM_KURS' };
  const sperre = kalenderSperre(stand, e.datum, kurs.standort_id);
  if (sperre) return { code: 'KALENDER_GESPERRT', detail: sperre.bezeichnung };
  if (einsatzZu(stand, 'kurs_id', e.kurs_id, e.datum, e.beginn, e.ende, e.id)) return { code: 'UEBERSCHNEIDUNG_KURS' };
  if (e.raum_id && einsatzZu(stand, 'raum_id', e.raum_id, e.datum, e.beginn, e.ende, e.id)) return { code: 'DOPPELBELEGUNG_RAUM' };
  if (e.dozent_id) {
    const d = stand.dozenten.find(x => x.id === e.dozent_id);
    if (!d?.aktiv) return { code: 'DOZENT_INAKTIV' };
    const abw = abwesenheitZu(stand, e.dozent_id, e.datum, e.beginn, e.ende);
    if (abw) return { code: 'DOZENT_ABWESEND', detail: abw.grund ?? undefined };
    if (!istQualifiziert(stand, e.dozent_id, e.kurs_knoten_id)) return { code: 'DOZENT_NICHT_QUALIFIZIERT' };
    if (einsatzZu(stand, 'dozent_id', e.dozent_id, e.datum, e.beginn, e.ende, e.id)) return { code: 'DOPPELBELEGUNG_DOZENT' };
  }
  return null;
}

// ------------------------------------------------------------------ Verfügbarkeit
export interface DozentLage {
  dozent: Dozent; qualifiziert: boolean; frei: boolean; standard: boolean;
  grund: string | null; ueWoche: number;
}
export function dozentenLage(stand: Stand, kursId: Id, knotenId: Id | null, datum: Datum, beginn: Uhrzeit, ende: Uhrzeit, ohneEinsatz?: Id): DozentLage[] {
  const fbKnoten = fachbereichsKnoten(stand, knotenId);
  const standardId = fbKnoten?.standard_dozent_id ?? null;
  const mo = montag(datum), so = plusTage(mo, 6);
  return stand.dozenten.filter(d => d.aktiv).map(d => {
    const qualifiziert = istQualifiziert(stand, d.id, knotenId);
    const abw = abwesenheitZu(stand, d.id, datum, beginn, ende);
    const belegt = einsatzZu(stand, 'dozent_id', d.id, datum, beginn, ende, ohneEinsatz);
    let grund: string | null = null;
    if (abw) grund = abw.quelle === 'absage' ? 'hat abgesagt' : `abwesend${abw.grund ? ': ' + abw.grund : ''}`;
    else if (belegt) grund = `im Einsatz: ${stand.kurse.find(k => k.id === belegt.kurs_id)?.kennung ?? ''}`;
    else if (!qualifiziert) grund = 'Fachbereich nicht hinterlegt';
    const ueWoche = stand.einsaetze.filter(e => e.dozent_id === d.id && aktiv(e) && e.datum >= mo && e.datum <= so).reduce((s, e) => s + e.ue, 0);
    return { dozent: d, qualifiziert, frei: !abw && !belegt, standard: d.id === standardId, grund, ueWoche };
  }).sort((a, b) =>
    Number(b.frei && b.qualifiziert) - Number(a.frei && a.qualifiziert) || Number(b.standard) - Number(a.standard)
    || Number(b.qualifiziert) - Number(a.qualifiziert) || a.ueWoche - b.ueWoche || a.dozent.nachname.localeCompare(b.dozent.nachname, 'de'));
}
export function freieRaeume(stand: Stand, standortId: Id | null, datum: Datum, beginn: Uhrzeit, ende: Uhrzeit, ohneEinsatz?: Id): { raum: Raum; frei: boolean }[] {
  return stand.raeume.filter(r => r.aktiv && (!standortId || r.standort_id === standortId))
    .map(raum => ({ raum, frei: !einsatzZu(stand, 'raum_id', raum.id, datum, beginn, ende, ohneEinsatz) }));
}

// ------------------------------------------------------------------ Planvorschlag
/**
 * Erzeugt Entwürfe für alle freien Zeitfenster eines Kurses im Zeitraum.
 * Verteilt die Fachbereiche nach Restbedarf, nimmt bevorzugt den Standard-Dozenten,
 * sonst den am wenigsten ausgelasteten qualifizierten freien Dozenten.
 */
export function planVorschlag(stand: Stand, kursId: Id, von: Datum, bis: Datum): EinsatzEntwurf[] {
  const kurs = stand.kurse.find(k => k.id === kursId);
  if (!kurs) return [];
  const arbeit: Stand = { ...stand, einsaetze: [...stand.einsaetze] };
  const vorschlaege: EinsatzEntwurf[] = [];
  const fbKnoten = stand.kursKnoten.filter(k => k.kurs_id === kursId && k.fachbereich_id && !stand.kursKnoten.some(p => p.id === k.parent_id && p.fachbereich_id));
  const fenster = kursFenster(stand, kurs);
  let zaehler = 0;
  for (let d = von > kurs.beginn ? von : kurs.beginn; d <= bis && d <= kurs.ende; d = plusTage(d, 1)) {
    if (!istUnterrichtstag(stand, kurs, d)) continue;
    for (const f of fenster) {
      if (einsatzZu(arbeit, 'kurs_id', kurs.id, d, f.beginn, f.ende)) continue;
      const si = sollIst(arbeit, kursId);
      const offen = fbKnoten.map(k => ({ k, rest: (si.get(k.id)?.soll ?? 0) - (si.get(k.id)?.geplant ?? 0) })).filter(x => x.rest > 0)
        .sort((a, b) => b.rest - a.rest);
      // nicht zweimal derselbe Fachbereich am selben Tag, solange es Alternativen gibt
      const heuteSchon = new Set(arbeit.einsaetze.filter(e => e.kurs_id === kursId && e.datum === d).map(e => fachbereichsKnoten(arbeit, e.kurs_knoten_id)?.id));
      const wahl = offen.find(x => !heuteSchon.has(x.k.id)) ?? offen[0];
      if (!wahl) return vorschlaege;
      const blatt = naechsterOffenerKnoten(arbeit, kursId, wahl.k.id) ?? wahl.k;
      const lage = dozentenLage(arbeit, kursId, blatt.id, d, f.beginn, f.ende).filter(l => l.frei && l.qualifiziert);
      const doz = lage.find(l => l.standard) ?? lage[0];
      let raum: Id | null = null;
      if (kurs.standard_form === 'praesenz') {
        const r = freieRaeume(arbeit, kurs.standort_id, d, f.beginn, f.ende);
        raum = r.find(x => x.raum.id === kurs.standard_raum_id && x.frei)?.raum.id ?? r.find(x => x.frei)?.raum.id ?? null;
      }
      const e: EinsatzEntwurf = {
        id: `vorschlag-${++zaehler}`, kurs_id: kursId, kurs_knoten_id: blatt.id, dozent_id: doz?.dozent.id ?? null,
        raum_id: raum, form: kurs.standard_form, datum: d, beginn: f.beginn, ende: f.ende, ue: f.ue, status: 'entwurf', hinweis: null,
      };
      vorschlaege.push(e);
      arbeit.einsaetze.push({ ...e, id: e.id!, mandant_id: kurs.mandant_id });
    }
  }
  return vorschlaege;
}
